import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Form, Row, Col } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
// icons
import { AiFillCloseCircle, AiFillPlusCircle } from 'react-icons/ai';
// api
import { deleteDelivery, editDelivery, createDelivery } from 'api/company';
// helpers
import { capitalizeText } from 'helpers';
// components
import CustomInput from 'components/backoffice/customInput';
import CustomCheckbox from 'components/backoffice/customCheckbox';
import DeleteModalCustom from 'components/backoffice/modals/DeleteModalCustom';
import SearchProvince from 'components/backoffice/customInput/SearchProvince';
import Button from 'components/userzone/buttons/ButtonCustom';
import SpinnerComponent from 'components/backoffice/SpinnerComponent';

const DeliveriesForm = ({ deliveryData, isLoading, isEditing }) => {
  const [localDeliveryData, setLocalDeliveryData] = useState(
    deliveryData || {
      postal_code_length: '',
      province: '',
      postal_codes: '',
      not_delivered: false,
      is_special_price: false,
      special_price: '0.00',
    },
  );
  const navigate = useNavigate();
  const { t } = useTranslation(['backoffice']);
  const [loading, setLoading] = useState(false);
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [errors, setErrors] = useState({
    postalCodeLength: '',
    province: '',
    postalCodes: ''
  });

  useEffect(() => {
    if (!localDeliveryData.is_special_price) {
      setLocalDeliveryData((prevData) => ({
        ...prevData,
        special_price: '0.00',
      }));
    }
  }, [localDeliveryData.is_special_price]);

  useEffect(() => {
    if (localDeliveryData.not_delivered) {
      setLocalDeliveryData((prevData) => ({
        ...prevData,
        postal_code_length: '',
        postal_codes: '',
      }));
    }
  }, [localDeliveryData.not_delivered]);

  useEffect(() => {
    if (deliveryData) {
      setLocalDeliveryData(deliveryData);
    }
  }, [deliveryData]);

  const handleProvinceChange = (selectedProvinceId) => {
    setLocalDeliveryData((prevData) => ({
      ...prevData,
      province: selectedProvinceId,
    }));
    setErrors((prev) => ({ ...prev, province: '' }));

  };

  const handleChange = (name, value) => {
    setLocalDeliveryData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleLengthChange = (value) => {
    const parsedValue = value.trim() === '' ? '' : parseInt(value, 10);

    if (errors.postalCodeLength) {
      setErrors((prev) => ({ ...prev, postalCodeLength: '' }));
    }
    setLocalDeliveryData((prevData) => ({
      ...prevData,
      postal_code_length: parsedValue,
    }));
  };

  const handlePostalCodesChange = (value, index) => {
    const codes = localDeliveryData.postal_codes.split(',');

    const maxLength = parseInt(localDeliveryData.postal_code_length, 10) || 0;

    const trimmedValue = value.slice(0, maxLength);
    codes[index] = trimmedValue;

    setLocalDeliveryData((prevData) => ({
      ...prevData,
      postal_codes: codes.join(','),
    }));

    if (errors.postalCodes) {
      setErrors((prev) => ({ ...prev, postalCodes: '' }));
    }
  };

  const addPostalCodeInput = () => {
    const codes = localDeliveryData.postal_codes ? localDeliveryData.postal_codes.split(',') : [];
    const allFilled = codes.every(code => code.trim() !== '');

    if (allFilled) {
      codes.push('');
      setLocalDeliveryData(prevData => ({
        ...prevData,
        postal_codes: codes.join(',')
      }));
    } else {
      toast.error(`${t('error')}: ${t('completeCode')}`, { theme: 'colored' });
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === ',') {
        e.preventDefault();
        const input = e.target;
        const index = Array.from(document.querySelectorAll('input')).indexOf(input);
        const codes = localDeliveryData.postal_codes.split(',');
        const isLastCodeEmpty = codes[codes.length - 1].trim() === '';
        if (index === codes.length - 1 && isLastCodeEmpty) {
          toast.error(`${t('error')}: ${t('CompleteCode')}`, { theme: 'colored' });
        } else {
          addPostalCodeInput();
        }
      }
    };
    document.querySelectorAll('input').forEach(input => {
      input.addEventListener('keydown', handleKeyDown);
    });
    return () => {
      document.querySelectorAll('input').forEach(input => {
        input.removeEventListener('keydown', handleKeyDown);
      });
    };
  }, [localDeliveryData.postal_codes]);

  const removePostalCodeInput = (index) => {
    const codes = localDeliveryData.postal_codes.split(',');
    codes.splice(index, 1);
    setLocalDeliveryData((prevData) => ({
      ...prevData,
      postal_codes: codes.join(','),
    }));
  };

  const handleResponse = (response, isEditing) => {
    const { status } = response;
    if (status === 200 || status === 201) {
      toast.success(isEditing ? t('editadocorrectamente') : t('creadocorrectamente'));
      navigate('/intranet/deliveries');
    } else {
      if (response.status === 400)
        toast.error(`${t('error')} ${status}: ${response.data.non_field_errors || t('mensajeerror')}`, { theme: 'colored' });
      else
        toast.error(`${t('error')} ${status}: ${t('mensajeerror')}`, { theme: 'colored' });
    }
  };

  const validateInputs = () => {
    const postalCodes = localDeliveryData.postal_codes.split(',');
    const maxLength = parseInt(localDeliveryData.postal_code_length, 10) || 0;

    const hasEmptyFields = postalCodes.some((code) => code.trim() === '');
    const invalidLengths = postalCodes.some((code) => code.length !== maxLength);

    if (!localDeliveryData.province) {
      setErrors((prev) => ({
        ...prev,
        province: `${t('camporequerido')}`,
      }));
      return false;
    }

    if (hasEmptyFields) {
      setErrors((prev) => ({
        ...prev,
        postalCodes: `${t('camporequerido')}`,
      }));
      return false;
    }

    if (invalidLengths) {
      setErrors((prev) => ({
        ...prev,
        postalCodes: `${t('quantityPostalCode')} ${maxLength} ${maxLength === 1 ? t('digit') : t('digits')
          }`,
      }));
      return false;
    }

    return true;
  };


  const onSubmit = async (e) => {
    e.preventDefault();
    setErrors({ postalCodeLength: '', province: '', postalCodes: '' });
    let hasErrors = false;

    if (!validateInputs()) return;

    try {
      setLoading(true);
      const response = isEditing
        ? await editDelivery(deliveryData.id, localDeliveryData)
        : await createDelivery(localDeliveryData);
      if (response?.status === 400) {
        const nonFieldErrors = response?.data?.non_field_errors;
        if (nonFieldErrors && nonFieldErrors.length > 0) {
          setErrors(prev => ({ ...prev, province: nonFieldErrors[0] }));
        }
      }
      handleResponse(response, isEditing);
    } catch (error) {
      console.log(error)
      setErrors(prev => ({ ...prev, postalCodes: error.message }));
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      const { status } = await deleteDelivery(localDeliveryData.id);
      if (status === 204) {
        toast.success(t('eliminadocorrectamente'));
        navigate('/intranet/deliveries', { replace: true });
      } else {
        toast.error(`${t('error')} ${status}: ${t('mensajeerror')}`, { theme: 'colored' });
      }
    } catch (error) {
      console.log(error);
      toast.error(`${t('error')}: ${error.message}`, { theme: 'colored' });
    }
    setLoading(false);
  };

  return (
    <div className="mt-5">
      <Form onSubmit={onSubmit}>
        <Row className="mt-4">
          <Col sm={12} md={12} lg={12} xl={4} className="mb-md-2 mb-lg-0">
            <Form.Group >
              <SearchProvince
                filterBy="nombre"
                placeholder={t('province')}
                onSelected={handleProvinceChange}
                defaultProvince={localDeliveryData.province}
              />
              {errors.province && <small className="text-danger">{errors.province}</small>}
            </Form.Group>
          </Col>
          <Col sm={12} md={12} lg={12} xl={2} className="mt-4 mt-xl-0 mb-md-2 mb-lg-0">
            <CustomCheckbox
              label={t('notDelivered')}
              checked={localDeliveryData.not_delivered}
              onChange={(e) => handleChange('not_delivered', e.target.checked)}
            />
          </Col>
          <Col sm={12} md={12} lg={12} xl={2} className="mt-4 mt-xl-0 mb-md-2 mb-lg-0">
            <CustomCheckbox
              label={t('specialPrice')}
              checked={localDeliveryData.is_special_price}
              onChange={(e) => handleChange('is_special_price', e.target.checked)}
            />
          </Col>
          {localDeliveryData.is_special_price && (
            <Col sm={12} md={12} lg={12}xl={4} className="mt-4 mt-xl-0 mb-md-2 mb-lg-0">
              <CustomInput
                label={t('specialPriceAmount')}
                type="number"
                step="0.01"
                name="special_price"
                value={localDeliveryData.special_price}
                onChange={(value) => handleChange('special_price', value)}
                placeholder={t('specialPriceAmount')}
              />
            </Col>
          )}
        </Row>
        <Row className="mt-4">
          <Col sm={12} md={12} lg={12} xl={4} className="mb-md-2 mb-lg-0">
            <Form.Group className="mb-3 mt-3">
              <CustomInput
                label={t('digitsPostalCode')}
                type="number"
                name="postal_code_length"
                value={localDeliveryData.postal_code_length}
                onChange={(value) => handleLengthChange(value)}
                disabled={localDeliveryData.not_delivered}
                placeholder={`${t('longitude')} ${t('postalcode').toLowerCase()}`}
              />
              {errors.postalCodeLength && <small className="text-danger">{errors.postalCodeLength}</small>}
            </Form.Group>
          </Col>
          <Col sm={12} md={12} lg={12} xl={8} className="mt-4 mt-xl-0 mb-md-2 mb-lg-0">
            <Form.Group className="mb-2 ">
              {
                localDeliveryData.postal_code_length && <Col sm={6} className='d-flex align-items-center justify-content-between'>
                  <Form.Label className='mb-0 me-2'>{t('postalcode')}</Form.Label>
                  <Button variant="link" className="bg-transparent"
                    disabled={localDeliveryData.postal_codes.split(',').every(code => code.trim() === '')}>
                    <AiFillPlusCircle
                      size={35}
                      color="#0d6efd"
                      className="cursor-pointer"
                      onClick={addPostalCodeInput}
                      title={`${t('add')} ${t('codigo')}`}
                    />
                  </Button>
                </Col>
              }
              {localDeliveryData.postal_code_length !== "" && localDeliveryData.postal_codes.split(',').map((code, index) => (
                <Row key={index} className="mb-2 d-flex align-items-end justify-content-start">
                  <Col xs={10} sm={10} md={10} lg={10} xl={6}>
                    <CustomInput
                      type="text"
                      value={code}
                      onChange={(value) => handlePostalCodesChange(value, index)}
                      maxLength={localDeliveryData.postal_code_length || undefined}
                      disabled={localDeliveryData.not_delivered}
                      placeholder={t('postalcode')}
                    />
                  </Col>
                  {(index !== 0 || code.trim() !== '') && !localDeliveryData.not_delivered && <Col xs={2} sm={2} md={2} lg={2} xl={4} className="d-flex align-items-end justify-content-end justify-content-xl-start mb-1 gap-1">
                    <AiFillCloseCircle
                      size={35}
                      color="#D9534F"
                      className="cursor-pointer"
                      onClick={() => removePostalCodeInput(index)}
                      title={`${t('delete')} ${t('codigo')}`}
                    />
                  </Col>}
                </Row>
              ))}
            </Form.Group>
          </Col>
        </Row>
        <Row className="mb-4">
          <Col sm={12}>
            <div className="mt-4 d-flex justify-content-end gap-3">
              <Button
                color="primary"
                type="submit"
                className="btn-intra  bg-primary-color btn btn-primary"
                loading={isLoading}
              >
                {t('save')}
                <SpinnerComponent loading={isLoading} />
              </Button>
              {isEditing && (
                <Button
                  color="secondary"
                  className="btn-intra bg-secondary-color btn btn-primary"
                  onClick={() => setOpenModalDelete(true)}
                >
                  {t('eliminar')}
                </Button>
              )}
            </div>
          </Col>
        </Row>
      </Form>
      <DeleteModalCustom
        openModal={openModalDelete}
        setOpenModal={setOpenModalDelete}
        handleDelete={handleDelete}
        deleteTitle={t('deletedelivery')}
        beforeTitle={t('edit', { title: capitalizeText(deliveryData?.province_name) })}
      />
    </div>
  );
};

DeliveriesForm.propTypes = {
  deliveryData: PropTypes.object,
  isLoading: PropTypes.bool,
  isEditing: PropTypes.bool,
};

export default DeliveriesForm;
